import { useMutation, useQuery } from "@tanstack/react-query";
import cx from "classnames";
import dayjs from "dayjs";
import { FC, SVGProps, useEffect, useState } from "react";
import {
  ActionFunction,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useSubmit,
} from "react-router-dom";

import { get, post } from "api";
import { Models, PostResult } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import AddedToCartCheck from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import BillingInfo from "atoms/icon/icons/ic_billing_info_small_ol.svg";
import MembershipsIcon from "atoms/icon/icons/ic_memberships_filled_ol.svg";
import Plus from "atoms/icon/icons/ic_plus_large_ol.svg";
import Warning from "atoms/icon/icons/ic_warning_ol.svg";
import Membership from "atoms/icon/icons/memberships.svg";
import MembershipFilled from "atoms/icon/icons/memberships_filled.svg";
import RepeatCash from "atoms/icon/icons/RepeatCash.svg";
import Rewards from "atoms/icon/icons/rewards.svg";
import Star from "atoms/icon/icons/star.svg";
import { Notification, useNotify } from "atoms/notification";
import SimpleCarousel from "atoms/simple-carousel/SimpleCarousel";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { TextButton } from "atoms/text-button/TextButton";
import { AnimatedBackground } from "components/AnimatedBackground/AnimatedBackground";
import { ExpandableItems } from "molecules/expandable-items/ExpandableItems";
import { InfoBlock } from "molecules/info-blocks/InfoBlock";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import { ListModal } from "molecules/modal-window/list-modal/ListModal";
import { TreatmentNotification } from "molecules/treatment-notification/TreatmentNotification";
import { IncludedMonthlyRewards } from "organisms/included-monthly-rewards/IncludedMonthlyRewards";
import MembershipPromo from "organisms/memberships-promo/MembershipsPromo";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import { queryClient } from "pages/Root";
import { IfFlag, useFeatureFlag } from "services/Flags";
import {
  getMembershipRewardGroupSubheader,
  getProductTitle,
  getProductTitleQuantity,
  hasBeautyBankAvailable,
} from "services/HelperService";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import { readFormData } from "toolbox/ReadFormData";
import { defineLoader } from "toolbox/Router";
import {
  beautyBankText,
  getBillingIntervalBase,
  getBillingPeriodIntervalTreatmentSelection,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";
import pluralize from "utils/pluralize";

import { TreatmentSelectorModal } from "../TreatmentSelectorModal";
import {
  getAvailableSelectableTreatmentsCount,
  hasAvailableTreatmentsToSelect,
  hasRolledOverTreatmentsToSelect,
} from "../utils";

import {
  canUserReactivateMembership,
  getDunningDays,
  getRemainingPaymentAttempts,
  handleUpdatePayment,
} from "./membershipHelper";

export const SelectableMonthlyTreatments = ({
  selectableTreatmentsRewardGroup,
  isMembershipCanceled,
  onSelectTreatmentClick,
  inactiveMembership,
  description,
  flagBeautyBank,
  svg,
  billingPeriodsBeforeFirstReward,
  membershipBillingPeriodInterval = "month",
  membershipBillingPeriodIntervalCount = 1,
  membershipExpirationDate,
}: {
  selectableTreatmentsRewardGroup: Models<"MembershipRewardGroupModel">;
  isMembershipCanceled?: boolean;
  onSelectTreatmentClick: (
    reward: Models<"MembershipRewardGroupModel">
  ) => void;
  inactiveMembership?: boolean | null;
  description?: string;
  flagBeautyBank?: boolean;
  svg: FC<SVGProps<SVGSVGElement>>;
  billingPeriodsBeforeFirstReward?: number;
  membershipBillingPeriodInterval: string;
  membershipBillingPeriodIntervalCount?: number;
  membershipExpirationDate?: string;
}) => {
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );

  const membershipRewards =
    selectableTreatmentsRewardGroup.membershipRewards || [];

  const hasBeautyBankCredit = hasBeautyBankAvailable(
    selectableTreatmentsRewardGroup
  );
  const beautyBankCreditAmount = formatCurrency(
    selectableTreatmentsRewardGroup.beautyBank?.creditAmount ?? 0,
    0
  );

  const beautyBankTerm = useFeatureFlag("beautyBankTerm");
  const isV3Enabled = true;

  const periodCount =
    membershipBillingPeriodIntervalCount *
    selectableTreatmentsRewardGroup.billingPeriodInterval;

  const treatmentSelectionText = getBillingPeriodIntervalTreatmentSelection(
    membershipBillingPeriodIntervalCount,
    membershipBillingPeriodInterval,
    isFlexibleMembershipsEnabled,
    periodCount,
    selectableTreatmentsRewardGroup.billingPeriodInterval
  );

  const treatmentSelectionSubheader = getMembershipRewardGroupSubheader(
    billingPeriodsBeforeFirstReward,
    membershipBillingPeriodIntervalCount,
    membershipBillingPeriodInterval
  );

  return (
    <div className={cx("px-4", isV3Enabled ? "py-3" : "mb-1 bg-white py-4")}>
      {!isV3Enabled && (
        <div className="mb-4 text-center">
          <div className="flex w-full justify-center">
            <Icon
              color={inactiveMembership ? "darkGrey" : "brandColor"}
              size="xsmall"
              svg={svg}
            />
          </div>
          <div
            className={cx(
              "py-4 text-sub1 uppercase",
              inactiveMembership ? "text-dark-grey" : "text-brand-color"
            )}
          >
            CHOOSE {selectableTreatmentsRewardGroup.quantityPerPeriod} OF THE
            FOLLOWING <br />
            EVERY {treatmentSelectionText}
          </div>
          <IfFlag flag="timeDelayedTreatmentGroups">
            <div className="text-body2 text-secondary">
              Starting on your{" "}
              <span className="text-bold2">{treatmentSelectionSubheader}</span>{" "}
              as a member
            </div>
          </IfFlag>
          {description && (
            <div
              className={cx(
                "text-body2",
                inactiveMembership ? "text-dark-grey" : "text-secondary"
              )}
            >
              {description}
            </div>
          )}
        </div>
      )}

      {isV3Enabled ? (
        <div className="rounded-xl border border-light-grey bg-white p-4 shadow-sm">
          <div className="space-y-2">
            <div className="flex flex-row items-center gap-2">
              <Icon
                color={inactiveMembership ? "darkGrey" : "brandColor"}
                size="x2small"
                svg={svg}
                className="flex-shrink-0"
              />
              <span className="text-h4 font-serif font-semibold text-primary">
                {selectableTreatmentsRewardGroup.quantityPerPeriod > 0
                  ? `Choose ${selectableTreatmentsRewardGroup.quantityPerPeriod} Every ${treatmentSelectionText}`
                  : `Choose Every ${treatmentSelectionText}`}
              </span>
            </div>

            <IfFlag flag="timeDelayedTreatmentGroups">
              {billingPeriodsBeforeFirstReward &&
              billingPeriodsBeforeFirstReward > 1 ? (
                <div className="text-body2 text-secondary">
                  Starting on your{" "}
                  <span className="text-bold2">
                    {treatmentSelectionSubheader}
                  </span>{" "}
                  as a member
                </div>
              ) : null}
            </IfFlag>

            {description && (
              <div className="pb-2 text-body2 text-secondary">
                {description}
              </div>
            )}

            {selectableTreatmentsRewardGroup.availableAt && (
              <div className="pb-2 text-body2 text-secondary">
                Next option available{" "}
                {dayjs(selectableTreatmentsRewardGroup.availableAt).format(
                  "MMM D, YYYY"
                )}
              </div>
            )}

            <div className="divide-border-one divide-y">
              {membershipRewards.map((mr) => {
                const title = getProductTitle(
                  mr.packageListingTitle,
                  mr.packageName
                );
                const quantityText = getProductTitleQuantity(
                  mr.quantity,
                  mr.packageUnitType
                );
                return (
                  <div key={mr.id} className="px-3 py-2.5">
                    <div className="text-left text-body2 text-primary">
                      {quantityText && mr.quantity > 1 && (
                        <span className="font-semibold">{quantityText} </span>
                      )}
                      <span className="font-semibold">{title}</span>
                      {mr.packageDescription && (
                        <p className="mt-1 line-clamp-4 font-normal text-secondary">
                          {mr.packageDescription}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              fullWidth
              onClick={() =>
                onSelectTreatmentClick(selectableTreatmentsRewardGroup)
              }
              disabled={
                selectableTreatmentsRewardGroup.availableTreatmentsAmount === 0
              }
            >
              {selectableTreatmentsRewardGroup.availableTreatmentsAmount === 1
                ? "Choose your benefit"
                : selectableTreatmentsRewardGroup.availableTreatmentsAmount > 0
                ? `${
                    selectableTreatmentsRewardGroup.availableTreatmentsAmount
                  } ${pluralize(
                    selectableTreatmentsRewardGroup.availableTreatmentsAmount,
                    "option",
                    "options"
                  )} available`
                : "Choose your benefit"}
            </Button>

            {!selectableTreatmentsRewardGroup.availableAt &&
              selectableTreatmentsRewardGroup.refreshDate && (
                <div className="text-center text-body2 text-secondary">
                  Refreshes{" "}
                  {dayjs(
                    membershipBillingPeriodInterval === "week" &&
                      membershipBillingPeriodIntervalCount &&
                      membershipBillingPeriodIntervalCount > 1 &&
                      membershipExpirationDate
                      ? membershipExpirationDate
                      : selectableTreatmentsRewardGroup.refreshDate
                  ).format("MMM D, YYYY")}
                </div>
              )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center rounded-lg border-[1px] border-solid border-light-grey px-2 pb-2 pt-6">
          <DescriptiveTag size="large" color="secondary">
            {`${
              selectableTreatmentsRewardGroup.availableTreatmentsAmount
            } ${pluralize(
              selectableTreatmentsRewardGroup.availableTreatmentsAmount,
              "treatment",
              "treatments"
            )}
            available`}
          </DescriptiveTag>
          {selectableTreatmentsRewardGroup.availableAt && (
            <div
              className={cx(
                "mb-[14px] mt-2 text-body2",
                inactiveMembership ? "text-dark-grey" : "text-secondary"
              )}
            >
              Next treatment available{" "}
              {dayjs(selectableTreatmentsRewardGroup.availableAt).format(
                "MMM D, YYYY"
              )}
            </div>
          )}
          {!selectableTreatmentsRewardGroup.availableAt &&
            selectableTreatmentsRewardGroup.refreshDate && (
              <div
                className={cx(
                  "mb-[14px] mt-2 text-body2",
                  inactiveMembership ? "text-dark-grey" : "text-secondary"
                )}
              >
                Refreshes{" "}
                {dayjs(
                  membershipBillingPeriodInterval === "week" &&
                    membershipBillingPeriodIntervalCount &&
                    membershipBillingPeriodIntervalCount > 1 &&
                    membershipExpirationDate
                    ? membershipExpirationDate
                    : selectableTreatmentsRewardGroup.refreshDate
                ).format("MMM D, YYYY")}
              </div>
            )}
          <div className="mb-4 w-full px-2">
            <ExpandableItems
              items={membershipRewards.map((mr) => ({
                id: mr.id,
                title:
                  getProductTitle(mr.packageListingTitle, mr.packageName) +
                  " " +
                  getProductTitleQuantity(mr.quantity, mr.packageUnitType),
                description: mr.packageDescription,
              }))}
            />
          </div>
          <Button
            fullWidth
            onClick={() =>
              onSelectTreatmentClick(selectableTreatmentsRewardGroup)
            }
            disabled={
              selectableTreatmentsRewardGroup.availableTreatmentsAmount === 0
            }
          >
            Select your treatment
          </Button>
        </div>
      )}
    </div>
  );
};

// Extracted Components
const Avatar = ({
  firstName,
  lastName,
  disabled,
}: {
  firstName?: string | null;
  lastName?: string | null;
  disabled?: boolean | null;
}) => {
  return (
    <div
      className={cx(
        "flex h-[72px] w-[72px] items-center justify-center rounded-full text-title text-white",
        disabled ? "bg-dark-grey" : "bg-brand-color"
      )}
    >
      {firstName?.charAt(0)}
      {lastName?.charAt(0)}
    </div>
  );
};

type MembershipCardV3Props = {
  membership: Models<"MembershipModel">;
  created?: string | null;
  canceled: boolean;
  canceledDate?: string | null;
  imageUrl?: string | null;
  isCanceled: boolean;
  isDunning: boolean;
  cancellationBadgeText?: string;
  textColor: string;
};

const MembershipCardV3 = ({
  membership,
  created,
  canceled,
  canceledDate,
  imageUrl,
  isCanceled,
  isDunning,
  cancellationBadgeText,
  textColor,
}: MembershipCardV3Props) => {
  return (
    <div
      className={cx(
        "relative aspect-[1.6/1] w-full overflow-hidden rounded-xl shadow-subtle",
        {
          "opacity-50": isCanceled,
        }
      )}
    >
      {/* Full membership image */}
      {imageUrl ? (
        <img
          alt={`${membership.name} membership`}
          src={imageUrl}
          className={cx("h-full w-full object-cover", isDunning && "grayscale")}
        />
      ) : (
        <div
          className={cx(
            "h-full w-full",
            isDunning ? "bg-dark-grey" : "bg-brand-color"
          )}
        />
      )}

      {/* Cancellation badge */}
      {cancellationBadgeText && (
        <div className="absolute left-4 top-4 rounded-md bg-red px-2 py-1">
          <div className="text-body3 font-bold text-white">
            {cancellationBadgeText}
          </div>
        </div>
      )}

      {/* White bottom section */}
      <div className="absolute bottom-0 left-0 right-0 bg-white px-6 py-1.5">
        <div className={cx("text-bold1", textColor)}>
          {membership.name.endsWith("Member") ||
          membership.name.endsWith("Membership")
            ? membership.name
            : `${membership.name} Member`}
        </div>
        <div className="text-body2 text-secondary">
          {canceled && canceledDate
            ? `Member from ${dayjs(created).format("MMM YYYY")} - ${dayjs(
                canceledDate
              ).format("MMM YYYY")}`
            : `Member since ${dayjs(created).format("MMM YYYY")}`}
        </div>
      </div>
    </div>
  );
};

type MembershipBenefitsSectionProps = {
  benefits: Models<"MembershipBenefitModel">[];
  membershipName: string;
  isCanceled: boolean;
};

const MembershipBenefitsSection = ({
  benefits,
  membershipName,
  isCanceled,
}: MembershipBenefitsSectionProps) => {
  return (
    <div className="px-4 pb-4 pt-3">
      <div
        className={cx(
          "relative overflow-hidden rounded-2xl bg-brand-color shadow-lg",
          {
            "opacity-50": isCanceled,
          }
        )}
      >
        <div className="relative flex flex-col gap-2 px-5 py-4">
          <div className="flex items-center gap-2">
            <Icon svg={Star} size="x2small" color="white" />
            <span className="text-h4 font-serif font-semibold text-white">
              Member-Only Benefits
            </span>
          </div>
          <div className="text-body2 font-semibold text-white/90">
            Enjoy exclusive member-only benefits as a {membershipName} member
          </div>
          <ul className="list-inside list-disc space-y-1 marker:text-white/70">
            {benefits.map((item) => (
              <li key={item.id} className="text-body2 text-white/90">
                {item.description}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

type WalletSignUpBonusCardProps = {
  availableTreatmentsAmount: number;
  onSelectClick: () => void;
};

const WalletSignUpBonusCard = ({
  availableTreatmentsAmount,
  onSelectClick,
}: WalletSignUpBonusCardProps) => {
  return (
    <div className="px-4 pb-3 pt-6">
      <div
        className="relative overflow-hidden rounded-2xl bg-brand-tint"
        style={{
          animation: "floatingShadow 3s ease-in-out infinite",
          boxShadow:
            "0 4px 12px rgb(var(--repeatmd-brand-color) / 0.15), 0 2px 6px rgb(var(--repeatmd-brand-color) / 0.1)",
        }}
      >
        <div className="relative flex flex-col gap-3 px-6 py-5">
          <div className="flex items-center gap-2">
            <Icon svg={Star} size="x2small" color="brandColor" />
            <div className="text-body2 font-bold uppercase tracking-wider text-primary">
              Sign-Up Bonus Unlocked
            </div>
          </div>
          <div className="text-body2 text-secondary">
            Unlock your choice of {availableTreatmentsAmount}{" "}
            {pluralize(availableTreatmentsAmount, "item", "items")}
          </div>
          <button
            onClick={onSelectClick}
            className="mt-1 flex items-center justify-center rounded-full border border-brand-color bg-white px-5 py-2.5 text-body1 font-semibold text-brand-color shadow-sm transition-transform active:scale-95"
          >
            Select Now →
          </button>
        </div>
      </div>
    </div>
  );
};

type WalletMilestoneBonusCardProps = {
  periodLabel: string;
  availableTreatmentsAmount: number;
  onSelectClick: () => void;
  animationDelay?: number;
};

const WalletMilestoneBonusCard = ({
  periodLabel,
  availableTreatmentsAmount,
  onSelectClick,
  animationDelay = 0,
}: WalletMilestoneBonusCardProps) => {
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-brand-tint"
      style={{
        animation: "floatingShadow 3s ease-in-out infinite",
        animationDelay: `${animationDelay}s`,
        boxShadow:
          "0 4px 12px rgb(var(--repeatmd-brand-color) / 0.15), 0 2px 6px rgb(var(--repeatmd-brand-color) / 0.1)",
      }}
    >
      <div className="relative flex flex-col gap-3 px-6 py-5">
        <div className="flex items-center gap-2">
          <Icon svg={Star} size="x2small" color="brandColor" />
          <div className="text-body2 font-bold uppercase tracking-wider text-primary">
            Milestone Bonus Unlocked
          </div>
        </div>
        <div className="font-serif text-h3 font-bold text-primary">
          {periodLabel} Milestone Bonus
        </div>
        <div className="text-body2 text-secondary">
          Unlock your choice of {availableTreatmentsAmount}{" "}
          {pluralize(availableTreatmentsAmount, "item", "items")}
        </div>
        <button
          onClick={onSelectClick}
          className="mt-1 flex items-center justify-center rounded-full border border-brand-color bg-white px-5 py-2.5 text-body1 font-semibold text-brand-color shadow-sm transition-transform active:scale-95"
        >
          Select Now →
        </button>
      </div>
    </div>
  );
};

const ManagePaymentMethod = ({
  dunningStage,
  enteredDunningAt,
  multipleMemberships,
  paymentMethodLast4,
  lastManualPaymentAttempt,
  onClick,
}: {
  dunningStage: number;
  enteredDunningAt: string | null | undefined;
  multipleMemberships?: boolean;
  paymentMethodLast4?: string | null | undefined;
  lastManualPaymentAttempt?: string | null | undefined;
  onClick?: () => void;
}) => {
  const isV3Enabled = true;

  const numberOfAttempts = getRemainingPaymentAttempts(dunningStage);
  const addedDunningDaysForNextPayment = getDunningDays(dunningStage);
  const addedDunningDaysForLastPayment = getDunningDays(dunningStage - 1);

  const lastManualPaymentAttemptDay = dayjs(lastManualPaymentAttempt);
  const lastAutomaticPaymentAttemptDay = dayjs(enteredDunningAt).add(
    addedDunningDaysForLastPayment,
    "day"
  );

  // V3 Design
  if (isV3Enabled) {
    return (
      <div className="mx-6 my-4">
        <div className="flex flex-col items-center gap-4 rounded-lg bg-[#8B0000] px-6 py-6 text-center">
          <div>
            <Icon svg={Warning} size="xlarge" color="white" />
          </div>
          <div className="flex flex-col gap-3 text-body2 text-white">
            <div>
              Your membership benefits are currently <strong>on hold</strong>{" "}
              due to a failed payment.
            </div>
            <div>
              We couldn&apos;t process your payment method{" "}
              {paymentMethodLast4 && `ending in ${paymentMethodLast4} `}on{" "}
              {lastManualPaymentAttemptDay >= lastAutomaticPaymentAttemptDay
                ? lastManualPaymentAttemptDay.format("MMM D, YYYY")
                : lastAutomaticPaymentAttemptDay.format("MMM D, YYYY")}
              . We&apos;ll automatically retry on{" "}
              {dayjs(enteredDunningAt)
                .add(addedDunningDaysForNextPayment, "day")
                .format("MMM D, YYYY")}
              . You have{" "}
              <strong>
                {numberOfAttempts}{" "}
                {pluralize(numberOfAttempts, "attempt", "attempts")}
              </strong>{" "}
              left before your membership is canceled and your benefits are
              lost. Don&apos;t wait—update your payment method to keep your
              perks active.
            </div>
          </div>
          <button
            onClick={onClick}
            className="flex h-11 w-full items-center justify-center rounded-full border-2 border-white bg-white px-6 text-button text-[#8B0000]"
            style={{ animation: "urgentPulse 1.5s ease-in-out infinite" }}
          >
            Update payment method
          </button>
        </div>
      </div>
    );
  }

  // Original Design (non-V3)
  return (
    <div className="flex flex-col items-center gap-3 px-3 py-5 text-center">
      <div>
        <Icon svg={Warning} size="small" color="red" />
      </div>
      <div className="text-bold1 text-red">
        Your membership benefits have been disabled due to failed payment.
      </div>
      <div className="text-body2 text-red">
        We were unable to process your payment method{" "}
        {paymentMethodLast4 && `ending in ${paymentMethodLast4} `} on{" "}
        {lastManualPaymentAttemptDay >= lastAutomaticPaymentAttemptDay
          ? lastManualPaymentAttemptDay.format("MMM D, YYYY")
          : lastAutomaticPaymentAttemptDay.format("MMM D, YYYY")}
        .
      </div>
      <div className="text-body2 text-red">
        We will try again automatically on{" "}
        {dayjs(enteredDunningAt)
          .add(addedDunningDaysForNextPayment, "day")
          .format("MMM D, YYYY")}
        .
      </div>
      <div className="text-body2 text-red">
        You have{" "}
        <strong>
          {numberOfAttempts} payment{" "}
          {pluralize(numberOfAttempts, "attempt", "attempts")}
        </strong>{" "}
        remaining until your membership is automatically canceled and you lose
        access to your membership benefits.
      </div>
      <Button style="danger" onClick={onClick}>
        Manage payment method
      </Button>
    </div>
  );
};

type LoaderData = Awaited<ReturnType<typeof walletMembershipLoader>>;
export const walletMembershipLoader = defineLoader(async () => {
  const userInfo = queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 1000 * 60 * 2,
  });
  const userMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  const userMembershipsStatus = queryClient.fetchQuery(
    ["/platform-user-memberships/status"],
    () => get("/platform-user-memberships/status")
  );

  const platform = queryClient.fetchQuery(["platform"], () => get("/platform"));

  const data = await waitObj({
    userMemberships,
    userInfo,
    userMembershipsStatus,
    platform,
  });

  if (data.userMemberships.length) {
    return { ...data, membershipFeatures: [], shopMemberships: [] };
  }

  const membershipFeatures = queryClient.fetchQuery(["/shop/memberships"], () =>
    get("/shop/memberships")
  );
  const shopMemberships = queryClient.fetchQuery(["/memberships"], () =>
    get("/memberships")
  );

  return waitObj({ membershipFeatures, shopMemberships, ...data });
});

type ActionResult = {
  successfullySelectedRewards: PostResult<"/platform-user-packages/{platformUserMembershipId}">;
  successfullyRepeatCashBeautyBank: PostResult<"/platform-user-memberships/{platformUserMembershipId}/redeem/repeat-cash">;
};

export const walletMembershipAction: ActionFunction = async ({ request }) => {
  const { data } = await readFormData<{ data: string }>(request);
  const { platformUserMembershipId, rewardIds, beautyBankId } =
    JSON.parse(data);

  if (beautyBankId && rewardIds.length === 0) {
    const successfullyRepeatCashBeautyBank = await post(
      "/platform-user-memberships/{platformUserMembershipId}/redeem/repeat-cash",
      {
        platformUserMembershipId,
        $data: beautyBankId,
      }
    );
    // Invalidate the user query to refresh the beauty bank balance
    await queryClient.invalidateQueries(["/user"]);
    return Response.json({
      successfullyRepeatCashBeautyBank,
    });
  } else {
    const successfullySelectedRewards = await post(
      "/platform-user-packages/{platformUserMembershipId}",
      {
        platformUserMembershipId,
        $data: rewardIds,
      }
    );

    return Response.json({
      successfullySelectedRewards,
    });
  }
};

const WalletMembershipPage = () => {
  const {
    userInfo,
    membershipFeatures,
    shopMemberships,
    userMembershipsStatus,
    platform,
    ...data
  } = useLoaderData() as LoaderData;

  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  const flagBeautyBank = useFeatureFlag("membershipBeautyBank");

  const [notificationProps] = useNotify();

  const submit = useSubmit();
  const actionData = useActionData() as ActionResult | undefined;
  const navigate = useNavigate();
  const [visibleMembershipId, setVisibleMembershipId] = useState(0);
  const [selectTreatmentModalStep, setSelectTreatmentModalStep] = useState(0);
  const [signUpBonusModalOpen, setSignUpBonusModalOpen] = useState(false);
  const [signUpBonusesSelected, setSignUpBonusesSelected] = useState<string[]>(
    []
  );
  const [showAddedToMyTretmentsDialog, setShowAddedToMyTretmentsDialog] =
    useState(false);
  const [openRewardGroup, setOpenRewardGroup] =
    useState<Models<"MembershipRewardGroupModel">>();
  const singleItemRedemption =
    platform.platformSettings?.membershipSingleItemRedemption;

  const [showReactivateMembershipDialog, setShowReactivateMembershipDialog] =
    useState(false);

  const [milestoneBonusOpen, setMilestoneBonusOpen] = useState<boolean>(false);
  const beautyBankTerm = useFeatureFlag("beautyBankTerm");

  const [milestoneBonusListModalOpen, setMilestoneBonusListModalOpen] =
    useState<boolean>(false);
  const [selectedMilestoneBonusId, setSelectedMilestoneBonusId] =
    useState<string>("");
  const [milestoneBonusesSelected, setMilestoneBonusesSelected] = useState<
    string[]
  >([]);

  const { mutate: reactivateMembership } = useMutation({
    mutationFn: async (platformUserMembershipId: string) => {
      return await post(
        "/platform-user-memberships/{platformUserMembershipId}/reactivate",
        {
          platformUserMembershipId: platformUserMembershipId,
        }
      );
    },
    onError: (error) => {
      console.log(error);
    },
    onSuccess: (response) => {
      setShowReactivateMembershipDialog(false);
      queryClient.setQueryData(
        ["/platform-user-memberships/status"],
        [response]
      );
      queryClient.invalidateQueries(["/platform-user-memberships"]);
    },
  });
  const location = useLocation();
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);

  const { data: userMemberships } = useQuery({
    queryKey: ["/platform-user-memberships"],
    queryFn: () => get("/platform-user-memberships"),
    initialData: data.userMemberships,
    select: (memberships) => {
      return [...memberships].sort((a, b) => {
        if (a.canceled !== b.canceled) {
          return a.canceled ? 1 : -1; // Active memberships first
        }
        return (
          new Date(b.created ?? "").getTime() -
          new Date(a.created ?? "").getTime()
        );
      });
    },
  });

  const { data: statuses } = useQuery({
    queryKey: ["/platform-user-memberships/status"],
    queryFn: () => get("/platform-user-memberships/status"),
    initialData: userMembershipsStatus,
  });

  const membershipInDunning = statuses?.find(
    (s) => s.dunningStage > 0 && s.dunningStage < 4 && s.canceled === false
  );

  const getDunningMembershipObject = () => {
    if (!membershipInDunning) {
      return {
        status: false,
        dunningStage: 0,
        enteredDunningAt: null,
        id: null,
      };
    }

    return {
      status: true,
      dunningStage: membershipInDunning.dunningStage,
      enteredDunningAt: membershipInDunning.enteredDunningAt,
      id: membershipInDunning.id,
    };
  };
  const dunningMembership = getDunningMembershipObject();

  useEffect(() => {
    if (actionData?.successfullyRepeatCashBeautyBank) {
      setSelectTreatmentModalStep(0);
      setOpenRewardGroup(undefined);
      navigate("/wallet/repeatcash");
    } else if (actionData?.successfullySelectedRewards?.platformUserPackages) {
      setSelectTreatmentModalStep(0);
      setOpenRewardGroup(undefined);
      setShowAddedToMyTretmentsDialog(true);
      setMilestoneBonusOpen(false);
    }
  }, [
    actionData?.successfullySelectedRewards?.platformUserPackages,
    actionData?.successfullyRepeatCashBeautyBank,
    navigate,
  ]);

  const handleCurrentlyVisibleMembership = (index: number) => {
    setVisibleMembershipId(index);
  };

  const isV3Enabled = true;

  if (!userMemberships.length) {
    const membershipFeature = membershipFeatures?.[0];

    return (
      <div className={isV3Enabled ? "flex-1" : "flex-1 bg-brand-color"}>
        <MembershipPromo
          {...membershipFeature}
          memberships={shopMemberships}
          useAnimatedBackground={isV3Enabled}
          fullscreen={isV3Enabled}
        />
      </div>
    );
  }

  const visibleMembership = userMemberships[visibleMembershipId];

  const visibleMembershipBillingPeriodInterval = parseBillingPeriodName(
    visibleMembership.membership.billingPeriodInterval
  );
  const visibleMembershipBillingPeriodIntervalName =
    isFlexibleMembershipsEnabled
      ? getBillingIntervalBase(visibleMembershipBillingPeriodInterval)
      : "month";

  const visibleMembershipHasAvailableTreatmentsToSelect =
    hasAvailableTreatmentsToSelect([visibleMembership]);

  const visibleMembershipHasRolledOverTreatmentsToSelect =
    hasRolledOverTreatmentsToSelect([visibleMembership]);

  const visibleMembershipAvailableTreatmentsCount =
    getAvailableSelectableTreatmentsCount([visibleMembership]);

  const addCanceledMembershipToCart = async () => {
    try {
      await post("/orders/current/lineitems", {
        membershipId: visibleMembership.membership.id,
      });
      queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });

      navigate("/cart");
    } catch (error) {
      console.error(
        "Not able to add order to cart error:",
        JSON.stringify(error)
      );
      throw new Error("Not able to add order to cart. Try again later.");
    }
  };

  const visibleMembershipStatus = statuses.find(
    (m) => m.id == visibleMembership.platformUserMembershipId
  );

  const visibleMembershipOnDunning =
    visibleMembershipStatus &&
    visibleMembershipStatus.dunningStage > 0 &&
    visibleMembership?.dunningStage < 4;

  const signUpBonus = visibleMembership.membership.membershipRewardGroups?.find(
    (group) => {
      return isFlexibleMembershipsEnabled
        ? group.billingPeriodsBeforeFirstReward === 0 &&
            group.billingPeriodInterval === 0 &&
            group.availableTreatmentsAmount > 0 &&
            !group.beautyBank?.creditAmount
        : group.monthsBeforeFirstReward === 0 &&
            group.periodMonths === 0 &&
            group.availableTreatmentsAmount > 0 &&
            !group.beautyBank?.creditAmount;
    }
  );

  const getSignUpBonusSelectionType = () => {
    if (
      signUpBonus?.availableTreatmentsAmount &&
      signUpBonus?.availableTreatmentsAmount > 1
    ) {
      return "multiple";
    } else {
      return "single";
    }
  };
  const signUpBonusSelectionType = getSignUpBonusSelectionType();

  const membershipMilestones =
    visibleMembership.membership.membershipRewardGroups?.filter(
      ({ billingPeriodInterval, periodMonths, beautyBank }) => {
        return isFlexibleMembershipsEnabled
          ? billingPeriodInterval === 0 && !beautyBank?.creditAmount
          : periodMonths === 0 && !beautyBank?.creditAmount;
      }
    );

  const getMilestoneBonusType = (id: string) => {
    if (!id || membershipMilestones?.length === 0) {
      return null;
    }

    const selectedMilestone = membershipMilestones?.find(
      (group) => group.id === id
    );

    return selectedMilestone && selectedMilestone?.availableTreatmentsAmount > 1
      ? "multiple"
      : "single";
  };

  const getMilestoneBonusData = (id: string) => {
    if (!id || membershipMilestones?.length === 0) {
      return null;
    }

    const selectedMilestone = membershipMilestones?.find(
      (group) => group.id === id
    );

    return selectedMilestone;
  };

  const handleMilestoneDetailsClick = (
    id: string,
    availableAt: string | null | undefined,
    availableTreatmentsAmount: number | null | undefined,
    billingPeriodsBeforeFirstReward: number | null | undefined,
    hasSelectableRewards: boolean | null | undefined
  ) => {
    if (availableAt == null && availableTreatmentsAmount == 0) {
      return;
    }

    if (billingPeriodsBeforeFirstReward == 0 && hasSelectableRewards) {
      setSignUpBonusModalOpen(true);
    } else if (
      availableTreatmentsAmount &&
      availableTreatmentsAmount > 0 &&
      hasSelectableRewards
    ) {
      setSelectedMilestoneBonusId(id);
      setMilestoneBonusOpen(true);
    } else if (availableTreatmentsAmount == 0 && availableAt != null) {
      setSelectedMilestoneBonusId(id);
      setMilestoneBonusListModalOpen(true);
    }
  };

  const getMilestoneBonusButtonLabel = (
    availableAt: string | null | undefined,
    availableTreatmentsAmount: number | null | undefined
  ) => {
    if (availableAt == null && availableTreatmentsAmount == 0)
      return "Received";
    if (availableTreatmentsAmount && availableTreatmentsAmount > 0)
      return "Select";

    return "Details";
  };

  const getMilestoneBonusStatus = (
    availableAt: string | null | undefined,
    receivedAt: string | null | undefined,
    availableTreatmentsAmount: number | null | undefined
  ) => {
    if (availableAt == null && availableTreatmentsAmount == 0)
      return "received";
    if (availableTreatmentsAmount && availableTreatmentsAmount > 0)
      return "available";
    if (receivedAt != null) return "received";
    return "upcoming";
  };

  const getMilestoneBonusDescription = (
    availableAt: string | null | undefined,
    billingPeriodBeforeFirstReward: number,
    quantityPerPeriod: number,
    availableTreatmentsAmount: number | null | undefined,
    receivedAt: string | null | undefined
  ) => {
    if (!billingPeriodBeforeFirstReward || !quantityPerPeriod) {
      return;
    }

    if (billingPeriodBeforeFirstReward == 0) {
      return `${quantityPerPeriod} ${pluralize(
        quantityPerPeriod,
        "treatment",
        "treatments"
      )} available`;
    }

    if (availableTreatmentsAmount && availableTreatmentsAmount > 0) {
      return `${availableTreatmentsAmount} ${pluralize(
        quantityPerPeriod,
        "treatment",
        "treatments"
      )} available`;
    }

    if (availableAt) return `Bonus available ${availableAt}`;

    return `Bonus received ${receivedAt}`;
  };

  const onSelectMilestoneBonus = async (clickedItem: string) => {
    let newMilestoneBonusesSelected;
    const milestoneBonusSelectedType = getMilestoneBonusType(
      selectedMilestoneBonusId
    );

    if (milestoneBonusSelectedType === "single") {
      newMilestoneBonusesSelected = [clickedItem];
    } else if (signUpBonusesSelected.includes(clickedItem)) {
      newMilestoneBonusesSelected = milestoneBonusesSelected.filter(
        (bonus) => bonus !== clickedItem
      );
    } else {
      newMilestoneBonusesSelected = [clickedItem, ...milestoneBonusesSelected];
    }
    setMilestoneBonusesSelected(newMilestoneBonusesSelected);
  };

  const getMilestoneListModalDescription = (
    hasSelectableRewards: boolean | undefined,
    visibleMembershipBillingPeriodInterval: string
  ) => {
    const quantityPerPeriod = getMilestoneBonusData(
      selectedMilestoneBonusId
    )?.quantityPerPeriod;
    const billingPeriodInterval = isFlexibleMembershipsEnabled
      ? (getMilestoneBonusData(selectedMilestoneBonusId)
          ?.billingPeriodsBeforeFirstReward ?? 1) *
        visibleMembership.membership.billingPeriodIntervalCount
      : getMilestoneBonusData(selectedMilestoneBonusId)
          ?.monthsBeforeFirstReward;

    if (!billingPeriodInterval || !quantityPerPeriod) {
      return;
    }

    if (hasSelectableRewards)
      return (
        <div>
          <span className="text-bold1">Select {quantityPerPeriod}</span> bonus{" "}
          {pluralize(quantityPerPeriod, "treatment", "treatments")} from the
          below after{" "}
          {billingPeriodInterval +
            " " +
            pluralize(
              billingPeriodInterval,
              visibleMembershipBillingPeriodIntervalName,
              `${visibleMembershipBillingPeriodIntervalName}s`
            )}{" "}
          as a {visibleMembership.membership.name} member
        </div>
      );

    return (
      <div>
        <span className="text-bold1">Receive {quantityPerPeriod}</span> bonus{" "}
        {pluralize(quantityPerPeriod, "treatment", "treatments")} after{" "}
        {billingPeriodInterval +
          " " +
          pluralize(
            billingPeriodInterval,
            visibleMembershipBillingPeriodInterval ?? "period",
            `${visibleMembershipBillingPeriodInterval}s`
          )}{" "}
        as a {visibleMembership.membership.name} member
      </div>
    );
  };

  const getMilestoneSelectModalSubtitle = () => {
    const availableTreatments = getMilestoneBonusData(
      selectedMilestoneBonusId
    )?.availableTreatmentsAmount;
    const billingPeriodInterval = isFlexibleMembershipsEnabled
      ? (getMilestoneBonusData(selectedMilestoneBonusId)
          ?.billingPeriodsBeforeFirstReward ?? 1) *
        visibleMembership.membership.billingPeriodIntervalCount
      : getMilestoneBonusData(selectedMilestoneBonusId)
          ?.monthsBeforeFirstReward;

    if (!availableTreatments) {
      return;
    }

    return (
      <div className="text-center text-body1 text-primary">
        <span className="text-bold1">Select {availableTreatments}</span>{" "}
        {pluralize(availableTreatments, "item", "items")} for your{" "}
        {billingPeriodInterval}-{visibleMembershipBillingPeriodIntervalName}{" "}
        bonus
      </div>
    );
  };

  const isMembershipAvailableOnUsersLocation = !!(
    userInfo.platformLocation?.id &&
    visibleMembership?.membership.platformLocationsIds?.includes(
      userInfo.platformLocation.id
    )
  );

  const checkIsShowReactivate = () => {
    return !!(
      canUserReactivateMembership(
        userMemberships,
        visibleMembership.membership.id,
        visibleMembership.platformUserMembershipId
      ) && isMembershipAvailableOnUsersLocation
    );
  };

  const manageMembershipsButtonClick = () => {
    navigate("/wallet/memberships/manage");
  };

  const activeMemberships = userMemberships.filter(
    ({ canceled }) => !canceled
  ).length;

  const rewardGroups =
    visibleMembership.membership.membershipRewardGroups ?? [];
  const beautyBankGroup = rewardGroups.find(
    ({ beautyBank }) => beautyBank?.creditAmount
  );

  const membershipHasBeautyBankConfiguration = () => {
    return !!beautyBankGroup && !beautyBankGroup.membershipRewards.length;
  };
  const hasBeautyBankOnMembership = membershipHasBeautyBankConfiguration();
  const beautyBankCreditAmountOnMembership = formatCurrency(
    beautyBankGroup?.beautyBank?.creditAmount ?? 0,
    0
  );

  const hasBeautyBankCreditOnRewardGroup =
    !!openRewardGroup && hasBeautyBankAvailable(openRewardGroup);

  const beautyBankCreditAmountOnRewardGroup = formatCurrency(
    openRewardGroup?.beautyBank?.creditAmount ?? 0,
    0
  );
  const beautyBankRepeatCashTitle = `${beautyBankTerm} RepeatCash`;

  const treatments =
    flagBeautyBank && hasBeautyBankCreditOnRewardGroup
      ? [
          ...(openRewardGroup.membershipRewards.map((rg) => ({
            id: rg.id,
            name: getProductTitle(rg.packageListingTitle, rg.packageName),
            description: rg.packageDescription ?? "",
            quantity: rg.quantity,
            packageUnitType: rg.packageUnitType,
          })) ?? []),
          {
            id: "beautyBank",
            name: `${beautyBankRepeatCashTitle} - ${beautyBankCreditAmountOnRewardGroup}`,
            description: `Bank ${beautyBankCreditAmountOnRewardGroup} for use on future services - use anytime!`,
            quantity: 1,
            packageUnitType: null,
          },
        ]
      : openRewardGroup?.membershipRewards.map((rg) => ({
          id: rg.id,
          name: getProductTitle(rg.packageListingTitle, rg.packageName),
          description: rg.packageDescription ?? "",
          quantity: rg.quantity,
          packageUnitType: rg.packageUnitType,
        }));

  const handleOnReactivateMembership = () => {
    if (
      visibleMembership.canceled ||
      (visibleMembershipStatus?.willCancelAt &&
        new Date(visibleMembershipStatus.willCancelAt) < new Date())
    ) {
      addCanceledMembershipToCart();
    } else {
      setShowReactivateMembershipDialog(true);
    }
  };

  const getMembershipBenefitsIconColor = () => {
    if (visibleMembershipOnDunning) {
      return "darkGrey";
    }
    return "brandColor";
  };

  const managePaymentMethodDunningStage = () => {
    if (visibleMembershipOnDunning) {
      return visibleMembershipStatus.dunningStage;
    }
    return dunningMembership.dunningStage;
  };

  const managePaymentMethodEnteredDunningAt = () => {
    if (visibleMembershipOnDunning) {
      return visibleMembershipStatus.enteredDunningAt;
    }
    return dunningMembership.enteredDunningAt ?? null;
  };

  const getTextColor = (defaultColor = "text-brand-color"): string => {
    return visibleMembershipOnDunning ? "text-dark-grey" : defaultColor;
  };

  const getMilestoneBonusTitle = () => {
    if (isFlexibleMembershipsEnabled) {
      return (
        (getMilestoneBonusData(selectedMilestoneBonusId)
          ?.billingPeriodsBeforeFirstReward ?? 1) *
          visibleMembership.membership.billingPeriodIntervalCount +
        `-${visibleMembershipBillingPeriodIntervalName} bonus`
      );
    }
    return (
      getMilestoneBonusData(selectedMilestoneBonusId)?.monthsBeforeFirstReward +
      "-month bonus"
    );
  };

  const areMembersshipsManagedExternally = userMemberships.some(
    (membership) => membership.expiration === "0001-01-01T00:00:00+00:00"
  );
  const showMembershipManagementButton =
    !areMembersshipsManagedExternally && !visibleMembershipStatus?.canceled;

  return (
    <div className={isV3Enabled ? "bg-background-two pb-20" : ""}>
      <Notification {...notificationProps} />
      {dunningMembership.status && (
        <ManagePaymentMethod
          dunningStage={managePaymentMethodDunningStage()}
          enteredDunningAt={managePaymentMethodEnteredDunningAt()}
          multipleMemberships={userMembershipsStatus.length > 1}
          onClick={manageMembershipsButtonClick}
        />
      )}
      {visibleMembershipOnDunning && visibleMembershipStatus && (
        <ManagePaymentMethod
          dunningStage={visibleMembershipStatus.dunningStage}
          enteredDunningAt={visibleMembershipStatus.enteredDunningAt}
          paymentMethodLast4={
            userMemberships.find((m) => m.id == visibleMembershipStatus.id)
              ?.paymentMethodDetails?.last4
          }
          lastManualPaymentAttempt={
            visibleMembershipStatus.manualPaymentLastAttemptAt
          }
          onClick={manageMembershipsButtonClick}
        />
      )}
      <div className="py-5 text-center text-body2 text-secondary">
        {activeMemberships} active{" "}
        {pluralize(activeMemberships, "membership", "memberships")}
      </div>
      <div>
        <SimpleCarousel
          aspectRatio="1.6/1"
          onUpdateIndex={handleCurrentlyVisibleMembership}
          slides={userMemberships.map(
            ({
              platformUserMembershipId,
              membership,
              created,
              canceled,
              canceledDate,
            }) =>
              isV3Enabled ? (
                // V3 Credit Card Design
                <div
                  key={platformUserMembershipId}
                  className={cx(
                    "relative aspect-[1.6/1] w-full overflow-hidden rounded-xl shadow-subtle",
                    {
                      "opacity-50": visibleMembership.canceled,
                    }
                  )}
                >
                  {/* Full membership image */}
                  {visibleMembership.membership.imageUrl ? (
                    <img
                      alt={`${membership.name} membership`}
                      src={visibleMembership.membership.imageUrl}
                      className={cx(
                        "h-full w-full object-cover",
                        visibleMembershipOnDunning && "grayscale"
                      )}
                    />
                  ) : (
                    <div
                      className={cx(
                        "h-full w-full",
                        visibleMembershipOnDunning
                          ? "bg-dark-grey"
                          : "bg-brand-color"
                      )}
                    />
                  )}

                  {/* Cancellation badge */}
                  {((visibleMembershipStatus &&
                    visibleMembershipStatus?.canceled) ||
                    visibleMembershipStatus?.willCancelAt) && (
                    <div className="absolute left-4 top-4 rounded-md bg-red px-2 py-1">
                      <div className="text-body3 font-bold text-white">
                        {(new Date(visibleMembershipStatus.willCancelAt!) <
                          new Date() ||
                          visibleMembershipStatus.canceled) &&
                          visibleMembershipStatus.canceled &&
                          `Canceled ${dayjs(
                            visibleMembership.canceledDate
                          ).format("MMM D, YYYY")}`}
                        {!visibleMembership.canceled &&
                          new Date(visibleMembershipStatus.willCancelAt!) >
                            new Date() &&
                          `Canceling ${dayjs(
                            visibleMembershipStatus.willCancelAt
                          ).format("MMM D, YYYY")}`}
                      </div>
                    </div>
                  )}

                  {/* White bottom section */}
                  <div className="absolute bottom-0 left-0 right-0 bg-white px-6 py-1.5">
                    <div className={cx("text-bold1", getTextColor())}>
                      {membership.name.endsWith("Member") ||
                      membership.name.endsWith("Membership")
                        ? membership.name
                        : `${membership.name} Member`}
                    </div>
                    <div className="text-body2 text-secondary">
                      {canceled && canceledDate
                        ? `Member from ${dayjs(created).format(
                            "MMM YYYY"
                          )} - ${dayjs(canceledDate).format("MMM YYYY")}`
                        : `Member since ${dayjs(created).format("MMM YYYY")}`}
                    </div>
                  </div>
                </div>
              ) : (
                // Original Design
                <div
                  key={platformUserMembershipId}
                  className={cx(
                    "flex flex-col items-center justify-center rounded-lg bg-white px-7 pb-5 pt-4 shadow-subtle",
                    {
                      "opacity-50": visibleMembership.canceled,
                    }
                  )}
                >
                  <div className="relative">
                    {visibleMembership.membership.imageUrl ? (
                      <div
                        className={cx(
                          visibleMembershipOnDunning && "grayscale"
                        )}
                      >
                        <img
                          alt="carousel membership"
                          src={visibleMembership.membership.imageUrl}
                          className="h-[72px] w-[72px] rounded-full object-cover"
                        />
                      </div>
                    ) : (
                      <Avatar
                        firstName={userInfo.firstName}
                        lastName={userInfo.lastName}
                        disabled={visibleMembershipOnDunning || false}
                      />
                    )}

                    {visibleMembershipHasAvailableTreatmentsToSelect && (
                      <div className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-red outline outline-[3px] outline-white" />
                    )}
                  </div>
                  <div className={cx("my-4 text-sub1", getTextColor())}>
                    {userInfo.firstName} {userInfo.lastName}
                  </div>
                  <DescriptiveTag
                    color={
                      visibleMembershipOnDunning ? "disabled" : "secondary"
                    }
                  >{`${membership.name} Member`}</DescriptiveTag>
                </div>
              )
          )}
        />

        {/* Reactivation and browse buttons */}
        {isV3Enabled &&
          ((visibleMembershipStatus && visibleMembershipStatus?.canceled) ||
            visibleMembershipStatus?.willCancelAt) && (
            <div className="mx-6 mb-6 mt-4">
              {visibleMembership.membership.active &&
                checkIsShowReactivate() && (
                  <Button fullWidth onClick={handleOnReactivateMembership}>
                    Reactivate
                  </Button>
                )}
              {(!visibleMembership.membership.active ||
                !isMembershipAvailableOnUsersLocation) && (
                <div className="flex flex-col gap-3 rounded-lg bg-white p-5 text-center shadow-subtle">
                  <div className="text-body text-secondary">
                    This membership is no longer offered at your location.
                  </div>
                  <Button
                    fullWidth
                    onClick={() => navigate("/shop/memberships")}
                  >
                    Browse Memberships
                  </Button>
                </div>
              )}
            </div>
          )}

        {/* Original deactivated membership UI (non-v3) */}
        {!isV3Enabled &&
          ((visibleMembershipStatus && visibleMembershipStatus?.canceled) ||
            visibleMembershipStatus?.willCancelAt) && (
            <div className="mx-6 mb-6 mt-2 flex flex-col items-center">
              <div className="mb-6 text-bold1 text-red">
                {(new Date(visibleMembershipStatus.willCancelAt!) <
                  new Date() ||
                  visibleMembershipStatus.canceled) &&
                  visibleMembershipStatus.canceled &&
                  `Membership canceled on ${dayjs(
                    visibleMembership.canceledDate
                  ).format("L")}`}
                {!visibleMembership.canceled &&
                  new Date(visibleMembershipStatus.willCancelAt!) >
                    new Date() &&
                  `Canceling on ${dayjs(
                    visibleMembershipStatus.willCancelAt
                  ).format("MMM D, YYYY")}`}
              </div>

              {visibleMembership.membership.active &&
                checkIsShowReactivate() && (
                  <Button fullWidth onClick={handleOnReactivateMembership}>
                    Reactivate
                  </Button>
                )}
              {(!visibleMembership.membership.active ||
                !isMembershipAvailableOnUsersLocation) && (
                <div className="flex flex-col items-center text-center">
                  <div className="mb-6 mt-2 text-secondary">
                    This membership is no longer available, click below to find
                    similar memberships
                  </div>

                  <Button
                    fullWidth
                    style="secondary"
                    onClick={() => navigate("/shop/memberships")}
                  >
                    Browse Memberships
                  </Button>
                </div>
              )}
            </div>
          )}

        {/* Member-Only Benefits Section */}
        {isV3Enabled &&
          !!visibleMembership.membership.membershipBenefits.length && (
            <MembershipBenefitsSection
              benefits={visibleMembership.membership.membershipBenefits}
              membershipName={visibleMembership.membership.name}
              isCanceled={visibleMembership.canceled}
            />
          )}

        {/* Non-V3 Member-Only Benefits Section */}
        {!isV3Enabled &&
          !!visibleMembership.membership.membershipBenefits.length && (
            <div
              className={cx("bg-white px-4 py-6", {
                "opacity-50": visibleMembership.canceled,
              })}
            >
              <div className="mb-2 flex flex-row items-center gap-2">
                <Icon
                  color={getMembershipBenefitsIconColor()}
                  size="x2small"
                  svg={Star}
                  className="flex-shrink-0"
                />
                <span
                  className={cx(
                    "text-h4 font-serif font-semibold",
                    getTextColor("text-primary")
                  )}
                >
                  Member-Only Benefits
                </span>
              </div>
              <div
                className={cx(
                  "pb-2 text-body2",
                  getTextColor("text-secondary")
                )}
              >
                Enjoy exclusive member-only benefits as a{" "}
                {visibleMembership.membership.name} member
              </div>
              <ul
                className={cx(
                  "list-outside list-disc pl-5",
                  visibleMembershipOnDunning
                    ? "marker:text-dark-grey"
                    : "marker:text-brand-color"
                )}
              >
                {visibleMembership.membership.membershipBenefits.map((item) => (
                  <li key={item.id}>
                    <div
                      className={cx(
                        "py-1 text-left text-body2",
                        getTextColor("text-primary")
                      )}
                    >
                      {item.description}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

        <div>
          {/* Sign-Up Bonus Card - Appears first if available */}
          {isV3Enabled && signUpBonus && (
            <WalletSignUpBonusCard
              availableTreatmentsAmount={signUpBonus.availableTreatmentsAmount}
              onSelectClick={() => setSignUpBonusModalOpen(true)}
            />
          )}

          {/* Milestone Bonus Cards - Appear after sign-up bonus if available */}
          {isV3Enabled &&
            membershipMilestones &&
            membershipMilestones.some(
              (rg) =>
                rg.hasSelectableRewards &&
                rg.availableTreatmentsAmount > 0 &&
                (isFlexibleMembershipsEnabled
                  ? rg.billingPeriodsBeforeFirstReward > 0
                  : rg.monthsBeforeFirstReward > 0)
            ) && (
              <div className="px-4 py-3">
                <div className="flex flex-col gap-3">
                  {membershipMilestones.map(
                    (rg, index) =>
                      rg.hasSelectableRewards &&
                      rg.availableTreatmentsAmount > 0 &&
                      (isFlexibleMembershipsEnabled
                        ? rg.billingPeriodsBeforeFirstReward > 0
                        : rg.monthsBeforeFirstReward > 0) && (
                        <WalletMilestoneBonusCard
                          key={index}
                          periodLabel={`${
                            isFlexibleMembershipsEnabled
                              ? rg.billingPeriodsBeforeFirstReward *
                                visibleMembership.membership
                                  .billingPeriodIntervalCount
                              : rg.monthsBeforeFirstReward
                          }-${visibleMembershipBillingPeriodIntervalName}`}
                          availableTreatmentsAmount={
                            rg.availableTreatmentsAmount
                          }
                          onSelectClick={() => {
                            handleMilestoneDetailsClick(
                              rg.id,
                              rg.availableAt,
                              rg.availableTreatmentsAmount,
                              isFlexibleMembershipsEnabled
                                ? rg.billingPeriodsBeforeFirstReward
                                : rg.monthsBeforeFirstReward,
                              rg.hasSelectableRewards
                            );
                          }}
                          animationDelay={index * 0.2}
                        />
                      )
                  )}
                </div>
              </div>
            )}

          {/* V3 Beauty Bank Card Design */}
          {isV3Enabled &&
            flagBeautyBank &&
            hasBeautyBankOnMembership &&
            visibleMembership &&
            (() => {
              // Use actual repeatCash balance from user data (in cents)
              const totalBanked = (userInfo.repeatCashBalance || 0) / 100;

              const billingPeriodCount =
                visibleMembership.membership.billingPeriodIntervalCount || 1;

              // Only show balance section if over $100
              const showBalance = totalBanked > 100;

              return (
                <div
                  className={cx("px-4 pb-4 pt-3", {
                    "opacity-50": visibleMembership.canceled,
                  })}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-black to-gray-800 shadow-lg">
                    {/* Animated background */}
                    <AnimatedBackground
                      mode="black"
                      durationSeconds={10}
                      className="absolute inset-0 h-full w-full opacity-40"
                    />

                    {/* Subtle card pattern/texture */}
                    <div
                      className="absolute inset-0 opacity-5"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                      }}
                    />

                    {/* Decorative plus signs - repositioned for better balance */}
                    <div className="absolute right-6 top-4 text-3xl font-light text-white opacity-10">
                      +
                    </div>
                    <div className="absolute left-6 top-8 text-2xl font-light text-white opacity-10">
                      +
                    </div>
                    <div className="absolute bottom-8 right-10 text-2xl font-light text-white opacity-10">
                      +
                    </div>

                    <div className="relative flex flex-col gap-5 px-6 py-5">
                      {/* Top section - RepeatCash Balance (only if over $100) */}
                      {showBalance && (
                        <>
                          <div className="flex flex-col gap-0.5">
                            <div className="text-body3 font-semibold uppercase tracking-wider text-white/50">
                              Your RepeatCash Balance
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon
                                svg={RepeatCash}
                                size="medium"
                                color="white"
                              />
                              <div className="font-serif text-h3 font-semibold text-white">
                                ${totalBanked.toLocaleString()}
                              </div>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="h-px w-full bg-white/10" />
                        </>
                      )}

                      {/* Bottom section - Monthly earnings (always shown) */}
                      <div className="flex flex-col gap-0.5">
                        <div className="text-body3 font-semibold uppercase tracking-wider text-white/50">
                          Every{" "}
                          {billingPeriodCount > 1
                            ? `${billingPeriodCount} ${visibleMembershipBillingPeriodIntervalName}s`
                            : visibleMembershipBillingPeriodIntervalName}{" "}
                          you earn
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon svg={RepeatCash} size="medium" color="white" />
                          <div className="font-serif text-h3 font-semibold text-white">
                            {beautyBankCreditAmountOnMembership}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* Original Beauty Bank Design (non-v3) */}
          {!isV3Enabled &&
            flagBeautyBank &&
            hasBeautyBankOnMembership &&
            visibleMembership && (
              <div
                className={cx("mb-1 flex flex-col gap-6 px-6 pb-6 pt-4", {
                  "opacity-50": visibleMembership.canceled,
                })}
              >
                <InfoBlock
                  icon={RepeatCash}
                  description={
                    <span className="text-bold1">
                      {beautyBankText(
                        isFlexibleMembershipsEnabled,
                        beautyBankCreditAmountOnMembership,
                        visibleMembershipBillingPeriodIntervalName,
                        visibleMembership.membership
                      )}
                    </span>
                  }
                />
              </div>
            )}

          {/* Rolled over treatments section for v3 (monthly treatments only, not milestones) */}

          {/* Non-v3 treatment notifications (old style) */}
          {!isV3Enabled &&
            (visibleMembershipHasRolledOverTreatmentsToSelect ||
              visibleMembershipHasAvailableTreatmentsToSelect ||
              signUpBonus) && (
              <div className="mb-1 flex flex-col gap-5 rounded-t-2xl bg-white px-6 pb-7 pt-6">
                <div className="text-center text-body1 text-primary">
                  You have treatments available!
                </div>
                <div className="-mx-2 flex flex-col gap-2">
                  {signUpBonus && (
                    <TreatmentNotification
                      buttonLabel="Select"
                      status="available"
                      type="signup"
                      icon={MembershipFilled}
                      onClick={() => setSignUpBonusModalOpen(true)}
                      text="Select your sign-up bonus now!"
                      description={`${
                        signUpBonus.availableTreatmentsAmount
                      } ${pluralize(
                        signUpBonus.availableTreatmentsAmount,
                        "treatment",
                        "treatments"
                      )} available`}
                    />
                  )}
                  {membershipMilestones && membershipMilestones?.length > 0 && (
                    <>
                      {membershipMilestones?.map(
                        (rg, index) =>
                          rg.hasSelectableRewards &&
                          rg.availableTreatmentsAmount > 0 &&
                          rg.monthsBeforeFirstReward > 0 && (
                            <div key={index}>
                              <TreatmentNotification
                                buttonLabel="Select"
                                status={getMilestoneBonusStatus(
                                  rg.availableAt,
                                  rg.receivedAt,
                                  rg.availableTreatmentsAmount
                                )}
                                type="milestone"
                                icon={MembershipFilled}
                                onClick={() => {
                                  handleMilestoneDetailsClick(
                                    rg.id,
                                    rg.availableAt,
                                    rg.availableTreatmentsAmount,
                                    isFlexibleMembershipsEnabled
                                      ? rg.billingPeriodsBeforeFirstReward
                                      : rg.monthsBeforeFirstReward,
                                    rg.hasSelectableRewards
                                  );
                                }}
                                text={`${
                                  isFlexibleMembershipsEnabled
                                    ? rg.billingPeriodsBeforeFirstReward *
                                      visibleMembership.membership
                                        .billingPeriodIntervalCount
                                    : rg.monthsBeforeFirstReward
                                }-${visibleMembershipBillingPeriodIntervalName} member bonus`}
                                description={getMilestoneBonusDescription(
                                  rg.availableAt,
                                  isFlexibleMembershipsEnabled
                                    ? rg.billingPeriodsBeforeFirstReward
                                    : rg.monthsBeforeFirstReward,
                                  rg.quantityPerPeriod,
                                  rg.availableTreatmentsAmount,
                                  rg.receivedAt
                                )}
                              />
                            </div>
                          )
                      )}
                    </>
                  )}
                  {(visibleMembershipHasRolledOverTreatmentsToSelect ||
                    visibleMembershipHasAvailableTreatmentsToSelect) && (
                    <TreatmentNotification
                      buttonLabel="View"
                      status="available"
                      type="monthly"
                      icon={Membership}
                      onClick={() =>
                        window.scrollTo(0, document.body.scrollHeight)
                      }
                      text={`You have ${visibleMembershipAvailableTreatmentsCount} ${pluralize(
                        visibleMembershipAvailableTreatmentsCount,
                        "treatment",
                        "treatments"
                      )} available`}
                    />
                  )}
                </div>
              </div>
            )}
        </div>

        {/* Monthly Treatments Section - After bonus cards */}
        {(visibleMembership.membership.membershipRewardGroups ?? [])
          .filter((rg) =>
            !!rg && isFlexibleMembershipsEnabled
              ? rg.billingPeriodInterval
              : rg.periodMonths
          )
          .map((rg, index) => (
            <>
              {!rg?.hasSelectableRewards ? (
                <IncludedMonthlyRewards
                  freeMembershipRewards={rg?.membershipRewards || []}
                  inactiveMembership={visibleMembershipOnDunning || false}
                  svg={index == 0 ? Rewards : Plus}
                  billingPeriodsBeforeFirstReward={
                    isFlexibleMembershipsEnabled
                      ? rg.billingPeriodsBeforeFirstReward
                      : rg.monthsBeforeFirstReward
                  }
                  billingPeriodInterval={rg.billingPeriodInterval}
                  membershipBillingPeriodInterval={
                    visibleMembershipBillingPeriodIntervalName
                  }
                  membershipBillingPeriodIntervalCount={
                    isFlexibleMembershipsEnabled
                      ? visibleMembership.membership.billingPeriodIntervalCount
                      : 1
                  }
                />
              ) : (
                <SelectableMonthlyTreatments
                  flagBeautyBank={flagBeautyBank}
                  selectableTreatmentsRewardGroup={rg}
                  isMembershipCanceled={visibleMembership.canceled}
                  onSelectTreatmentClick={(rewardGroup) => {
                    if (rewardGroup.membershipRewards.length) {
                      setSelectTreatmentModalStep(1);
                      setOpenRewardGroup(rewardGroup);
                    }
                  }}
                  inactiveMembership={visibleMembershipOnDunning || false}
                  svg={index == 0 ? Rewards : Plus}
                  membershipBillingPeriodInterval={
                    visibleMembershipBillingPeriodIntervalName
                  }
                  membershipBillingPeriodIntervalCount={
                    isFlexibleMembershipsEnabled
                      ? visibleMembership.membership.billingPeriodIntervalCount
                      : 1
                  }
                  billingPeriodsBeforeFirstReward={
                    isFlexibleMembershipsEnabled
                      ? rg.billingPeriodsBeforeFirstReward
                      : rg.monthsBeforeFirstReward
                  }
                  membershipExpirationDate={visibleMembership.expiration}
                />
              )}
            </>
          ))}

        {membershipMilestones &&
          membershipMilestones?.length > 0 &&
          (() => {
            // Calculate how many months the user has been a member
            const createdDate = dayjs(visibleMembership.created);
            const now = dayjs();
            const currentMonthsActive = now.diff(createdDate, "month");

            // Find the next milestone that hasn't been received yet and is AFTER current months
            // Exclude sign-up bonus (0 month milestone) and already received milestones
            const upcomingMilestones = membershipMilestones
              .filter((rg) => {
                const months = isFlexibleMembershipsEnabled
                  ? rg.billingPeriodsBeforeFirstReward *
                    visibleMembership.membership.billingPeriodIntervalCount
                  : rg.monthsBeforeFirstReward;
                // Filter out received, sign-up bonuses (0 months), and milestones at or before current months
                return (
                  !rg.receivedAt && months > 0 && months > currentMonthsActive
                );
              })
              .sort((a, b) => {
                const aMonths = isFlexibleMembershipsEnabled
                  ? a.billingPeriodsBeforeFirstReward *
                    visibleMembership.membership.billingPeriodIntervalCount
                  : a.monthsBeforeFirstReward;
                const bMonths = isFlexibleMembershipsEnabled
                  ? b.billingPeriodsBeforeFirstReward *
                    visibleMembership.membership.billingPeriodIntervalCount
                  : b.monthsBeforeFirstReward;
                return aMonths - bMonths;
              });

            const nextMilestone = upcomingMilestones[0];

            if (!nextMilestone) return null;

            const monthsUntilMilestone = isFlexibleMembershipsEnabled
              ? nextMilestone.billingPeriodsBeforeFirstReward *
                visibleMembership.membership.billingPeriodIntervalCount
              : nextMilestone.monthsBeforeFirstReward;

            return (
              <IfFlag flag="timeDelayedTreatmentGroups">
                {isV3Enabled ? (
                  <div className="px-4 pb-4 pt-3">
                    <button
                      onClick={() => {
                        handleMilestoneDetailsClick(
                          nextMilestone.id,
                          nextMilestone.availableAt,
                          nextMilestone.availableTreatmentsAmount,
                          nextMilestone.monthsBeforeFirstReward,
                          nextMilestone.hasSelectableRewards
                        );
                      }}
                      className="w-full text-left"
                    >
                      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 shadow-md">
                        {/* Subtle card pattern/texture */}
                        <div
                          className="absolute inset-0 opacity-5"
                          style={{
                            backgroundImage:
                              "radial-gradient(circle at 2px 2px, rgba(0,0,0,0.3) 1px, transparent 0)",
                            backgroundSize: "32px 32px",
                          }}
                        />

                        {/* Decorative plus signs */}
                        <div className="absolute right-6 top-4 text-3xl font-light text-gray-400 opacity-20">
                          +
                        </div>
                        <div className="absolute left-6 top-8 text-2xl font-light text-gray-400 opacity-20">
                          +
                        </div>
                        <div className="absolute bottom-8 right-10 text-2xl font-light text-gray-400 opacity-20">
                          +
                        </div>

                        <div className="relative flex flex-col gap-2 px-6 py-6">
                          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Your Next Milestone
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon
                              svg={MembershipFilled}
                              size="medium"
                              color="secondaryText"
                            />
                            <div className="font-serif text-h3 font-semibold text-primary">
                              {monthsUntilMilestone}-
                              {visibleMembershipBillingPeriodIntervalName}{" "}
                              Milestone Bonus
                            </div>
                          </div>
                          <div className="pt-1 text-body2 text-secondary">
                            {nextMilestone.quantityPerPeriod > 0 ? (
                              <>
                                Unlock your choice of{" "}
                                {nextMilestone.quantityPerPeriod}{" "}
                                {pluralize(
                                  nextMilestone.quantityPerPeriod,
                                  "item",
                                  "items"
                                )}{" "}
                                {nextMilestone.availableAt &&
                                  `on ${nextMilestone.availableAt}`}
                              </>
                            ) : (
                              <>
                                Unlock your choice of benefits{" "}
                                {nextMilestone.availableAt &&
                                  `on ${nextMilestone.availableAt}`}
                              </>
                            )}
                          </div>
                          <div className="pt-2 text-bold2 text-brand-color">
                            View details →
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="mb-1 bg-white px-4 py-6">
                    <div className="text-center">
                      <div className="flex w-full justify-center">
                        <Icon
                          color="brandColor"
                          size="xsmall"
                          svg={MembershipsIcon}
                        />
                      </div>
                      <div className="pb-2 pt-6 text-sub1 uppercase text-brand-color">
                        Membership Milestones
                      </div>
                      <div className="text-body1 text-secondary">
                        One-time bonuses that you earn at certain milestones as
                        a {visibleMembership.membership.name} member
                      </div>
                      <div className="flex flex-col gap-2 pt-3">
                        {membershipMilestones?.map((rg, index) => (
                          <div key={index}>
                            <TreatmentNotification
                              buttonLabel={getMilestoneBonusButtonLabel(
                                rg.availableAt,
                                rg.availableTreatmentsAmount
                              )}
                              status={getMilestoneBonusStatus(
                                rg.availableAt,
                                rg.receivedAt,
                                rg.availableTreatmentsAmount
                              )}
                              type="milestone"
                              icon={MembershipFilled}
                              onClick={() => {
                                handleMilestoneDetailsClick(
                                  rg.id,
                                  rg.availableAt,
                                  rg.availableTreatmentsAmount,
                                  rg.monthsBeforeFirstReward,
                                  rg.hasSelectableRewards
                                );
                              }}
                              text={
                                (
                                  isFlexibleMembershipsEnabled
                                    ? rg.billingPeriodsBeforeFirstReward === 0
                                    : rg.monthsBeforeFirstReward == 0
                                )
                                  ? "Select your sign-up bonus now!"
                                  : `${
                                      isFlexibleMembershipsEnabled
                                        ? rg.billingPeriodsBeforeFirstReward *
                                          visibleMembership.membership
                                            .billingPeriodIntervalCount
                                        : rg.monthsBeforeFirstReward
                                    }-${visibleMembershipBillingPeriodIntervalName} member bonus`
                              }
                              description={getMilestoneBonusDescription(
                                rg.availableAt,
                                isFlexibleMembershipsEnabled
                                  ? rg.billingPeriodsBeforeFirstReward
                                  : rg.monthsBeforeFirstReward,
                                rg.quantityPerPeriod,
                                rg.availableTreatmentsAmount,
                                rg.receivedAt
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </IfFlag>
            );
          })()}

        {openRewardGroup?.membershipRewards && (
          <TreatmentSelectorModal
            beautyBankCreditAmount={beautyBankCreditAmountOnRewardGroup}
            treatments={treatments ?? []}
            singleItemRedemption={singleItemRedemption}
            onSubmit={(ids) => {
              submit(
                {
                  data: JSON.stringify({
                    rewardIds: ids.filter((id) => id !== "beautyBank"),
                    beautyBankCreditAmount:
                      openRewardGroup?.beautyBank?.creditAmount,
                    beautyBankId: openRewardGroup?.beautyBank?.id,
                    platformUserMembershipId: visibleMembership.id,
                  }),
                },
                { method: "post" }
              );
            }}
            onNext={() => {
              if (!singleItemRedemption) {
                setSelectTreatmentModalStep((step) => step + 1);
              }
            }}
            onCancel={() => {
              setOpenRewardGroup(undefined);
              setSelectTreatmentModalStep(0);
            }}
            onBack={() => {
              if (!singleItemRedemption) {
                setSelectTreatmentModalStep((step) => step - 1);
              }
            }}
            currentStep={selectTreatmentModalStep}
            stepCount={
              !singleItemRedemption
                ? openRewardGroup.availableTreatmentsAmount
                : 1
            }
          />
        )}

        {showAddedToMyTretmentsDialog && (
          <ActionModal
            size="small"
            open={showAddedToMyTretmentsDialog}
            title="Added to my treatments"
            description={actionData?.successfullySelectedRewards.platformUserPackages.map(
              (r) => (
                <div key={r.packageId} className="mb-2 px-2">
                  <div className="mb-1.5 flex items-center justify-between text-body1 text-primary">
                    <div>{r.variantName}</div>
                    <div>Free</div>
                  </div>
                  <div className="text-left text-body2 text-secondary">
                    {r.totalTreatmentAmount}{" "}
                    {r.totalTreatmentAmount > 1
                      ? r.packageUnitType?.pluralUnitType
                      : r.packageUnitType?.singularUnitType}
                  </div>
                </div>
              )
            )}
            headerIconSvg={AddedToCartCheck}
            firstButtonLabel={`View my ${pluralize(
              actionData?.successfullySelectedRewards.platformUserPackages
                .length ?? 0,
              "treatment",
              "treatments"
            )}`}
            onClose={() => setShowAddedToMyTretmentsDialog(false)}
            onFirstButtonClick={() => navigate("/wallet/treatments")}
          />
        )}

        {showReactivateMembershipDialog && (
          <ActionModal
            open={showReactivateMembershipDialog}
            title="Reactivate your membership?"
            description={`Your deactivation will be canceled and your membership will remain active. Your next payment will be due on 
          ${dayjs(visibleMembership.expiration).format("MMM D, YYYY")}`}
            size="small"
            firstButtonLabel="Reactivate"
            onFirstButtonClick={() =>
              reactivateMembership(visibleMembership.platformUserMembershipId)
            }
            onClose={() => setShowReactivateMembershipDialog(false)}
          />
        )}

        {signUpBonus && signUpBonus.membershipRewards?.length > 0 && (
          <ModalSelectorList
            open={signUpBonusModalOpen}
            size="fullscreen"
            type="products"
            selectionType={signUpBonusSelectionType}
            title="Select your sign-up bonus"
            subtitle={
              <div className="text-center text-body1 text-primary">
                <span className="text-bold1">
                  Select {signUpBonus.availableTreatmentsAmount}
                </span>{" "}
                {pluralize(
                  signUpBonus.availableTreatmentsAmount,
                  "item",
                  "items"
                )}{" "}
                for your sign-up bonus
              </div>
            }
            items={signUpBonus.membershipRewards.map((item) => ({
              id: item.id,
              title: item.packageName || "",
              subtitle: `${item.quantity} ${pluralize(
                item.quantity,
                item.packageUnitType?.singularUnitType ?? "unit",
                item.packageUnitType?.pluralUnitType ?? "units"
              )}`,
              disabled:
                !signUpBonusesSelected.includes(item.id) &&
                signUpBonusesSelected.length >=
                  signUpBonus.availableTreatmentsAmount,
            }))}
            checkedItems={signUpBonusesSelected}
            firstButtonLabel="Confirm"
            onFirstButtonClick={() =>
              submit(
                {
                  data: JSON.stringify({
                    rewardIds: signUpBonusesSelected,
                    platformUserMembershipId: visibleMembership.id,
                  }),
                },
                { method: "post" }
              )
            }
            firstButtonDisabled={
              signUpBonusesSelected.length !==
              signUpBonus.availableTreatmentsAmount
            }
            secondButtonLabel="Cancel"
            onSecondButtonClick={() => {
              setSignUpBonusesSelected([]);
              setSignUpBonusModalOpen(false);
            }}
            onClose={() => {
              setSignUpBonusesSelected([]);
              setSignUpBonusModalOpen(false);
            }}
            onSelect={(clickedItem) => {
              let newSignUpBonusesSelected;
              if (signUpBonusSelectionType === "single") {
                newSignUpBonusesSelected = [clickedItem];
              } else if (signUpBonusesSelected.includes(clickedItem)) {
                newSignUpBonusesSelected = signUpBonusesSelected.filter(
                  (bonus) => bonus !== clickedItem
                );
              } else {
                newSignUpBonusesSelected = [
                  clickedItem,
                  ...signUpBonusesSelected,
                ];
              }
              setSignUpBonusesSelected(newSignUpBonusesSelected);
            }}
          />
        )}

        {membershipMilestones &&
          membershipMilestones?.length > 0 &&
          selectedMilestoneBonusId && (
            <>
              <ModalSelectorList
                open={milestoneBonusOpen}
                size="fullscreen"
                type="products"
                selectionType={
                  getMilestoneBonusType(selectedMilestoneBonusId) ?? "single"
                }
                title={"Select your " + getMilestoneBonusTitle()}
                subtitle={getMilestoneSelectModalSubtitle()}
                items={
                  getMilestoneBonusData(
                    selectedMilestoneBonusId
                  )?.membershipRewards?.map((item) => ({
                    id: item.id,
                    title: item.packageName || "",
                    subtitle: `${item.quantity} ${pluralize(
                      item.quantity,
                      item.packageUnitType?.singularUnitType ?? "unit",
                      item.packageUnitType?.pluralUnitType ?? "units"
                    )}`,
                    disabled: false,
                  })) || []
                }
                checkedItems={milestoneBonusesSelected}
                firstButtonLabel="Confirm"
                onFirstButtonClick={() => {
                  submit(
                    {
                      data: JSON.stringify({
                        rewardIds: milestoneBonusesSelected,
                        platformUserMembershipId: visibleMembership.id,
                      }),
                    },
                    { method: "post" }
                  );
                }}
                firstButtonDisabled={
                  milestoneBonusesSelected.length !==
                  membershipMilestones.find(
                    (mm) => mm.id === selectedMilestoneBonusId
                  )?.availableTreatmentsAmount
                }
                secondButtonLabel="Cancel"
                onSecondButtonClick={() => {
                  setMilestoneBonusesSelected([]);
                  setMilestoneBonusOpen(false);
                }}
                onClose={() => {
                  setMilestoneBonusesSelected([]);
                  setMilestoneBonusOpen(false);
                }}
                onSelect={(clickedItem) => {
                  onSelectMilestoneBonus(clickedItem);
                }}
              />

              <ListModal
                open={milestoneBonusListModalOpen}
                onClose={() => setMilestoneBonusListModalOpen(false)}
                size="fullscreen"
                title={getMilestoneBonusTitle()?.toString() ?? ""}
                firstButtonLabel="Okay"
                onFirstButtonClick={() => {
                  setMilestoneBonusListModalOpen(false);
                }}
                firstButtonDisabled={false}
              >
                <div className="flex flex-col gap-5 p-6">
                  <div className="text-center text-body1 text-primary">
                    {getMilestoneListModalDescription(
                      getMilestoneBonusData(selectedMilestoneBonusId)
                        ?.hasSelectableRewards,
                      visibleMembershipBillingPeriodIntervalName
                    )}
                  </div>
                  <div>
                    <ExpandableItems
                      items={
                        getMilestoneBonusData(
                          selectedMilestoneBonusId
                        )?.membershipRewards?.map((item) => {
                          return {
                            id: item.id,
                            title: getProductTitle(
                              item.packageListingTitle,
                              item.packageName
                            ),
                            description: item.packageDescription,
                            subtitle: `${item.quantity} ${pluralize(
                              item.quantity,
                              item.packageUnitType?.singularUnitType ?? "unit",
                              item.packageUnitType?.pluralUnitType ?? "units"
                            )}`,
                          };
                        }) || []
                      }
                    />
                  </div>
                </div>
              </ListModal>
            </>
          )}

        <ActionModal
          size="small"
          open={showUpdatePaymentModal}
          title="Update payment method"
          description="You will be sent to our payment provider to update your subscription payment method. Any updates will take effect on your next payment date."
          firstButtonLabel="Update payment method"
          onFirstButtonClick={() =>
            handleUpdatePayment(
              visibleMembership.platformUserMembershipId,
              platform,
              () => {
                setShowUpdatePaymentModal(false);
                navigate(location.pathname);
              }
            )
          }
          headerIconSvg={BillingInfo}
          secondButtonLabel="Cancel"
          onSecondButtonClick={() => setShowUpdatePaymentModal(false)}
          onClose={() => setShowUpdatePaymentModal(false)}
        />
        {showMembershipManagementButton && (
          <div className="my-6 flex justify-center">
            <TextButton
              endIconSVG={ChevronRight}
              onClick={() => manageMembershipsButtonClick()}
            >
              Manage memberships
            </TextButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletMembershipPage;
